import React from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2 } from "lucide-react";
import {
    Tooltip, ResponsiveContainer,
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";

export default function CompareDrafts({ open, onOpenChange, drafts, totalsByDraft, selectedDraftId, onSelect, targetPrice }) {
    // Always compare every available draft side by side, across both modes
    // (Quotation and Actual). Each column is tagged with its mode so they stay
    // distinguishable.
    const filtered = drafts;
    // Build chart rows from already-loaded totalsByDraft (no extra API calls)
    const chartRows = filtered.map((d) => {
        const t = totalsByDraft[d.draft_id] || {};
        const label = `${d.name || d.draft_id.slice(-4)} · ${d.mode === "zbc" ? "Actual" : "Quote"}`;
        return {
            id: d.draft_id,
            label,
            RM: Number(t.rm_total || 0),
            BOP: Number(t.bop_total || 0),
            Process: Number(t.process_total || 0),
            Outsource: Number(t.outsource_total || 0),
            Investment: Number(t.amortised_investment_per_piece || 0),
            Overhead: Number(t.overhead_fixed || 0),
            Profit: Number(t.profit_value || 0),
            Total: Number(t.final_cost || 0),
        };
    });
    const CHART_SERIES = [
        { key: "RM", color: "#002FA7" },
        { key: "BOP", color: "#3F51B5" },
        { key: "Process", color: "#0EA5E9" },
        { key: "Outsource", color: "#475569" },
        { key: "Investment", color: "#9C27B0" },
        { key: "Overhead", color: "#FFB703" },
        { key: "Profit", color: "#2A9D8F" },
    ];
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-none max-w-6xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Compare Drafts</DialogTitle>
                    <div className="text-xs text-zinc-500 mt-1" data-testid="compare-scope-note">
                        All {filtered.length} draft{filtered.length === 1 ? "" : "s"} · Quotation and Actual, side by side
                    </div>
                </DialogHeader>
                {chartRows.length >= 2 && (
                    <div className="border border-zinc-200 p-3 mb-3" data-testid="compare-chart">
                        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mb-2">
                            <span>Stacked cost breakdown · {chartRows.length} drafts</span>
                        </div>
                        <div style={{ height: 260 }}>
                            <ResponsiveContainer>
                                <BarChart data={chartRows} margin={{ top: 6, right: 12, left: 4, bottom: 32 }}>
                                    <CartesianGrid stroke="#f0f0f0" />
                                    <XAxis dataKey="label" angle={-12} textAnchor="end" interval={0} tick={{ fontSize: 10 }} height={60} />
                                    <YAxis tick={{ fontSize: 10 }} />
                                    <Tooltip formatter={(v) => `₹ ${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`} />
                                    <Legend wrapperStyle={{ fontSize: 10 }} />
                                    {CHART_SERIES.map(({ key, color }) => (
                                        <Bar key={key} dataKey={key} stackId="cost" fill={color} name={key} isAnimationActive={false} />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}
                <div className="overflow-x-auto">
                    <table className="w-full sheet-table" data-testid="compare-drafts-table">
                        <thead>
                            <tr>
                                <th>Component</th>
                                {filtered.map((d) => (
                                    <th key={d.draft_id}>
                                        <div className="flex items-center justify-between gap-2">
                                            <span>{d.name}</span>
                                            <span className={`text-[9px] uppercase tracking-wider ${d.mode === "zbc" ? "text-[#9C27B0]" : "text-zinc-400"}`}>
                                                {d.mode === "zbc" ? "Actual" : "Quote"}
                                            </span>
                                            {selectedDraftId === d.draft_id && <CheckCircle2 size={14} className="text-[#2A9D8F]" />}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {[
                                ["RM", "rm_total"],
                                ["BOP", "bop_total"],
                                ["Process", "process_total"],
                                ["Outsource", "outsource_total"],
                                ["Investment (amort.)", "amortised_investment_per_piece"],
                                ["Overhead (fixed)", "overhead_fixed"],
                                ["Profit", "profit_value"],
                                ["Value addition", "value_addition"],
                                ["Total / piece", "final_cost"],
                            ].map(([label, key]) => (
                                <tr
                                    key={key}
                                    className={
                                        key === "final_cost"
                                            ? "font-semibold bg-zinc-50"
                                            : key === "value_addition"
                                                ? "italic text-zinc-600 border-t border-zinc-200"
                                                : ""
                                    }
                                >
                                    <td>{label}</td>
                                    {filtered.map((d) => {
                                        // Every value comes from the respective draft's backend
                                        // costing sheet (totalsByDraft is sourced from /totals),
                                        // so these match the draft's own totals exactly.
                                        const t = totalsByDraft[d.draft_id] || {};
                                        const val = Number(t[key] || 0);
                                        const total = Number(t.final_cost || 0);
                                        // % share of this draft's total cost — not shown on the
                                        // Total row itself, which is the 100% base.
                                        const pct = key === "final_cost" || total <= 0 ? null : (val / total) * 100;
                                        return (
                                            <td key={d.draft_id} className="font-mono">
                                                ₹ {val.toFixed(2)}
                                                {pct !== null && (
                                                    <span className="block text-[10px] font-normal not-italic text-zinc-400">
                                                        {pct.toFixed(1)}% of total
                                                    </span>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                            <tr>
                                <td className="font-semibold">Profit margin %</td>
                                {filtered.map((d) => {
                                    const t = totalsByDraft[d.draft_id] || {};
                                    const pct = (t.profit_value || 0) / Math.max(t.final_cost || 1, 0.0001) * 100;
                                    return <td key={d.draft_id} className="font-mono">{pct.toFixed(1)}%</td>;
                                })}
                            </tr>
                            <tr>
                                <td className="font-semibold">Variance vs Target (₹ {targetPrice.toFixed(2)})</td>
                                {filtered.map((d) => {
                                    const v = targetPrice - (totalsByDraft[d.draft_id]?.final_cost || 0);
                                    return <td key={d.draft_id} className={`font-mono font-semibold ${v >= 0 ? "text-[#2A9D8F]" : "text-[#E63946]"}`}>₹ {v.toFixed(2)}</td>;
                                })}
                            </tr>
                            <tr>
                                <td></td>
                                {filtered.map((d) => (
                                    <td key={d.draft_id}>
                                        <Button
                                            variant={selectedDraftId === d.draft_id ? "default" : "outline"}
                                            className={`rounded-none h-7 text-xs ${selectedDraftId === d.draft_id ? "bg-[#2A9D8F] hover:bg-[#218377]" : ""}`}
                                            onClick={() => onSelect(d.draft_id)}
                                            data-testid={`compare-select-${d.draft_id}`}
                                        >
                                            {selectedDraftId === d.draft_id ? "Selected" : "Use for Proposal"}
                                        </Button>
                                    </td>
                                ))}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </DialogContent>
        </Dialog>
    );
}
